import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  Divider,
  Grid,
  IconButton,
  LinearProgress,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
  Collapse,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import StopCircleIcon from "@mui/icons-material/StopCircle";
import ReplayIcon from "@mui/icons-material/Replay";
import MemoryIcon from "@mui/icons-material/Memory";
import StorageIcon from "@mui/icons-material/Storage";
import FiberManualRecordIcon from "@mui/icons-material/FiberManualRecord";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import BlockIcon from "@mui/icons-material/Block";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import AgentResourcesModal from "./components/AgentResourcesModal";
import http from "../../utils/http-common";
import { socket } from "../../utils/socket";
import Title from "../../components/Title";
import { pageHelp } from "../../components/help/pageHelp";
import { useNotification } from "../../context/NotificationContext";

// ---- types -----------------------------------------------------------------
interface RunningCase {
  id: number;
  title: string;
  suite_id: number;
  tc_result: string;
  sys_result: string;
  type: string;
  types: string[];
  started?: string | null;
  report_exists?: boolean;
}
interface AgentQueue {
  id: number;
  uuid: string;
  title: string;
  enabled: boolean;
  status: string;
  type: string;
  host: string;
  os_type: string;
  logic_group: string;
  capabilities: string[];
  resources_count: number;
  running: RunningCase[];
  running_count: number;
  updated_at?: string | null;
}
interface QueueResponse {
  agents: AgentQueue[];
  queued: RunningCase[];
  queued_count: number;
}

// ---- helpers ---------------------------------------------------------------
const CAP_COLORS: Record<string, string> = {
  hil: "#7B1FA2",
  sil: "#1565C0",
  mobile: "#00838F",
  software: "#2E7D32",
  other: "#616161",
};
const capColor = (c: string) => CAP_COLORS[c] || CAP_COLORS.other;

// Human-readable meaning of each capability, shown in the legend.
const CAP_MEANING: Record<string, string> = {
  hil: "HIL — real hardware (boards, FPGA)",
  sil: "SIL — software emulator",
  mobile: "Mobile — phone via Appium",
  software: "Software — no device needed",
};

const lastSeen = (iso?: string | null): { label: string; online: boolean } => {
  if (!iso) return { label: "never", online: false };
  const norm = /[zZ]|[+-]\d\d:?\d\d$/.test(iso) ? iso : iso + "Z";
  const sec = Math.max(0, (Date.now() - new Date(norm).getTime()) / 1000);
  const online = sec < 120;
  let label: string;
  if (sec < 60) label = "just now";
  else if (sec < 3600) label = `${Math.floor(sec / 60)}m ago`;
  else if (sec < 86400) label = `${Math.floor(sec / 3600)}h ago`;
  else label = `${Math.floor(sec / 86400)}d ago`;
  return { label, online };
};

const resultColor = (r: string): string => {
  const v = (r || "").toUpperCase();
  if (v === "PASSED") return "#2E7D32";
  if (["FAILED", "FAILED_ENSURE"].includes(v)) return "#F57C00";
  if (["CMD_ERROR", "INTERNAL_ERROR", "INTERRUPTED", "ABORTED"].includes(v)) return "#C62828";
  if (["RESERVED", "RUNNING"].includes(v)) return "#1565C0";
  return "#757575";
};

const CapBadge = ({ cap }: { cap: string }) => (
  <Tooltip title={CAP_MEANING[cap] || cap} arrow>
    <Chip
      size="small"
      label={cap.toUpperCase()}
      sx={{ bgcolor: capColor(cap), color: "#fff", fontWeight: 700, letterSpacing: 0.4 }}
    />
  </Tooltip>
);

const ResultChip = ({ value }: { value: string }) => (
  <Chip size="small" label={value} sx={{ bgcolor: resultColor(value), color: "#fff", fontWeight: 600 }} />
);

// ---- small KPI tile --------------------------------------------------------
const Kpi = ({ label, value, color, hint }: { label: string; value: number; color: string; hint?: string }) => (
  <Tooltip title={hint || ""} arrow disableHoverListener={!hint}>
    <Paper
      variant="outlined"
      sx={{ px: 2, py: 1, borderRadius: 2, minWidth: 100, display: "flex", flexDirection: "column", gap: 0.25, cursor: hint ? "help" : "default" }}
    >
      <Typography variant="h5" fontWeight={800} sx={{ color, lineHeight: 1 }}>
        {value}
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600, letterSpacing: 0.3 }}>
        {label}
      </Typography>
    </Paper>
  </Tooltip>
);

// ---- case row --------------------------------------------------------------
const CaseRow = ({
  c,
  onAbort,
  onRequeue,
}: {
  c: RunningCase;
  onAbort: (id: number) => void;
  onRequeue: (id: number) => void;
}) => (
  <Stack
    direction="row"
    alignItems="center"
    spacing={1}
    sx={{ py: 0.75, borderBottom: "1px solid", borderColor: "divider", "&:last-of-type": { borderBottom: 0 } }}
  >
    <Chip size="small" label={(c.type || "?").toUpperCase()} sx={{ bgcolor: capColor(c.type), color: "#fff", minWidth: 64 }} />
    <Box sx={{ flexGrow: 1, minWidth: 0 }}>
      <Typography variant="body2" noWrap title={c.title} sx={{ fontWeight: 600 }}>
        {c.title}
      </Typography>
      <Typography variant="caption" color="text.secondary">
        case #{c.id} · suite {c.suite_id}
      </Typography>
    </Box>
    <ResultChip value={c.tc_result} />
    <Tooltip title="Abort / stop this test">
      <IconButton size="small" color="error" onClick={() => onAbort(c.id)}>
        <StopCircleIcon fontSize="small" />
      </IconButton>
    </Tooltip>
    <Tooltip title="Re-queue (start again)">
      <IconButton size="small" color="primary" onClick={() => onRequeue(c.id)}>
        <ReplayIcon fontSize="small" />
      </IconButton>
    </Tooltip>
  </Stack>
);

// ---- page ------------------------------------------------------------------
const AgentsQueue: React.FC = () => {
  const { notify } = useNotification();
  const [data, setData] = useState<QueueResponse>({ agents: [], queued: [], queued_count: 0 });
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState<number | null>(null);
  const [resModal, setResModal] = useState<{ open: boolean; id?: number; title?: string }>({ open: false });
  const [showHelp, setShowHelp] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await http.get<QueueResponse>("/agent/queue");
      setData(res.data);
    } catch (e) {
      console.error("Failed to load agent queue", e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 5000);
    const onUpdate = () => load();
    socket.on("agent_queue_updated", onUpdate);
    return () => {
      clearInterval(interval);
      socket.off("agent_queue_updated", onUpdate);
    };
  }, [load]);

  const act = async (id: number, path: string, msg: string, type: "success" | "info" = "success") => {
    setBusy(id);
    try {
      await http.post(`/agent/${id}/${path}`);
      notify(msg, type);
      await load();
    } catch {
      notify("Action failed", "error");
    } finally {
      setBusy(null);
    }
  };
  const approveAgent = (a: AgentQueue) => act(a.id, "approve", `Agent "${a.title}" approved & enabled`);
  const declineAgent = (a: AgentQueue) => act(a.id, "decline", `Agent "${a.title}" declined`, "info");
  const toggleAgent = (a: AgentQueue) =>
    act(a.id, a.enabled ? "disable" : "enable", `Agent "${a.title}" ${a.enabled ? "disabled" : "enabled"}`);

  const abortCase = async (id: number) => {
    try {
      await http.post(`/case/${id}/abort`);
      notify(`Case #${id} aborted`, "info");
      load();
    } catch {
      notify("Failed to abort case", "error");
    }
  };
  const requeueCase = async (id: number) => {
    try {
      await http.post(`/case/${id}/requeue`);
      notify(`Case #${id} re-queued`, "success");
      load();
    } catch {
      notify("Failed to re-queue case", "error");
    }
  };

  const pending = useMemo(() => data.agents.filter((a) => a.status === "pending"), [data.agents]);
  const active = useMemo(() => data.agents.filter((a) => a.status !== "pending"), [data.agents]);
  const stats = useMemo(() => {
    const online = data.agents.filter((a) => lastSeen(a.updated_at).online).length;
    const running = data.agents.reduce((n, a) => n + a.running_count, 0);
    return { total: data.agents.length, online, pending: pending.length, running };
  }, [data.agents, pending.length]);

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
        <Title help={pageHelp.agents.help} helpItems={pageHelp.agents.items}>
          Agents
        </Title>
        <Stack direction="row" spacing={1} alignItems="center">
          <Button
            size="small"
            variant="text"
            startIcon={<InfoOutlinedIcon />}
            endIcon={showHelp ? <ExpandLessIcon /> : <ExpandMoreIcon />}
            onClick={() => setShowHelp((v) => !v)}
            sx={{ textTransform: "none" }}
          >
            How this works
          </Button>
          <Tooltip title="Refresh">
            <IconButton onClick={load}>
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Stack>
      </Stack>

      {/* Explainer + legend */}
      <Collapse in={showHelp}>
        <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, mb: 2, bgcolor: "background.level1" }}>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            An <b>agent</b> is a machine that runs your tests. It registers here, waits for{" "}
            <b>approval</b>, then advertises the <b>resources</b> it has (a board, an emulator, a phone…).
            Each queued case is automatically <b>routed</b> to an enabled agent whose resources match what the
            test needs — so a case only runs where it can.
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap alignItems="center">
            <Typography variant="caption" sx={{ fontWeight: 800, color: "text.secondary", mr: 0.5 }}>
              CAPABILITIES:
            </Typography>
            {["hil", "sil", "mobile", "software"].map((c) => (
              <Stack key={c} direction="row" spacing={0.75} alignItems="center">
                <Box sx={{ width: 12, height: 12, borderRadius: "3px", bgcolor: capColor(c) }} />
                <Typography variant="caption" sx={{ color: "text.secondary" }}>
                  {CAP_MEANING[c]}
                </Typography>
              </Stack>
            ))}
          </Stack>
        </Paper>
      </Collapse>

      {/* KPI summary */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 3 }} flexWrap="wrap" useFlexGap>
        <Kpi label="AGENTS" value={stats.total} color="#111827" hint="Total registered agents (approved + pending)" />
        <Kpi label="ONLINE" value={stats.online} color="#16a34a" hint="Agents with a heartbeat in the last 2 minutes" />
        <Kpi label="PENDING" value={stats.pending} color="#d97706" hint="Registered agents waiting for your approval" />
        <Kpi label="RUNNING" value={stats.running} color="#1565C0" hint="Test cases currently executing on agents" />
        <Kpi label="QUEUED" value={data.queued_count} color="#6b7280" hint="Cases waiting for a free, matching agent" />
      </Stack>

      {loading && <LinearProgress sx={{ mb: 1 }} />}

      {/* Pending approval — surfaced prominently */}
      {pending.length > 0 && (
        <Card
          sx={{
            mb: 3,
            border: "1px solid",
            borderColor: "#f59e0b",
            bgcolor: "rgba(245,158,11,0.06)",
          }}
        >
          <CardHeader
            avatar={<HourglassEmptyIcon sx={{ color: "#d97706" }} />}
            title={`Pending approval (${pending.length})`}
            subheader="New agents have registered and are waiting to be approved before they can run cases."
            titleTypographyProps={{ variant: "h6", fontWeight: 700 }}
          />
          <CardContent sx={{ pt: 0 }}>
            <Stack spacing={1.25}>
              {pending.map((a) => {
                const seen = lastSeen(a.updated_at);
                return (
                  <Paper
                    key={a.id}
                    variant="outlined"
                    sx={{ p: 1.5, borderRadius: 2, display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap" }}
                  >
                    <FiberManualRecordIcon sx={{ fontSize: 11, color: seen.online ? "#16a34a" : "#bdbdbd" }} />
                    <Box sx={{ flexGrow: 1, minWidth: 180 }}>
                      <Typography variant="subtitle2" fontWeight={700} noWrap title={a.title}>
                        {a.title}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {a.os_type} · {a.host} · {a.resources_count} resources · last seen {seen.label}
                      </Typography>
                    </Box>
                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setResModal({ open: true, id: a.id, title: a.title })}
                    >
                      Resources
                    </Button>
                    <Button
                      size="small"
                      color="error"
                      variant="outlined"
                      startIcon={<BlockIcon />}
                      disabled={busy === a.id}
                      onClick={() => declineAgent(a)}
                    >
                      Decline
                    </Button>
                    <Button
                      size="small"
                      color="success"
                      variant="contained"
                      startIcon={<CheckCircleIcon />}
                      disabled={busy === a.id}
                      onClick={() => approveAgent(a)}
                    >
                      Approve
                    </Button>
                  </Paper>
                );
              })}
            </Stack>
          </CardContent>
        </Card>
      )}

      {/* Queue (not yet routed) */}
      <Card sx={{ mb: 3 }}>
        <CardHeader
          title={`Queue (${data.queued_count})`}
          subheader="Cases waiting to be picked up by a capable agent"
          titleTypographyProps={{ variant: "h6" }}
        />
        <CardContent sx={{ pt: 0 }}>
          {data.queued.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No pending cases.
            </Typography>
          ) : (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {data.queued.map((c) => (
                <Chip
                  key={c.id}
                  label={`${c.title} · ${(c.type || "?").toUpperCase()}`}
                  sx={{ bgcolor: capColor(c.type), color: "#fff", mb: 1 }}
                />
              ))}
            </Stack>
          )}
        </CardContent>
      </Card>

      {/* Active agents grid */}
      {active.length === 0 ? (
        <Typography variant="body2" color="text.secondary" sx={{ px: 0.5 }}>
          No approved agents yet.
        </Typography>
      ) : (
        <Grid container spacing={2}>
          {active.map((a) => {
            const seen = lastSeen(a.updated_at);
            const declined = a.status === "decline";
            return (
              <Grid item xs={12} md={6} lg={4} key={a.id}>
                <Card sx={{ height: "100%", opacity: a.enabled && !declined ? 1 : 0.62 }}>
                  <CardHeader
                    avatar={<MemoryIcon color={a.enabled && !declined ? "primary" : "disabled"} />}
                    title={
                      <Stack direction="row" alignItems="center" spacing={0.75}>
                        <Tooltip title={seen.online ? "Online (recent heartbeat)" : `Last seen ${seen.label}`}>
                          <FiberManualRecordIcon sx={{ fontSize: 11, color: seen.online ? "#16a34a" : "#bdbdbd" }} />
                        </Tooltip>
                        <Typography variant="subtitle1" fontWeight={700} noWrap title={a.title}>
                          {a.title}
                        </Typography>
                      </Stack>
                    }
                    subheader={`${a.os_type} · ${a.host} · last seen ${seen.label}`}
                    action={
                      declined ? (
                        <Tooltip title="Approve this agent">
                          <Button
                            size="small"
                            color="success"
                            startIcon={<CheckCircleIcon />}
                            disabled={busy === a.id}
                            onClick={() => approveAgent(a)}
                          >
                            Approve
                          </Button>
                        </Tooltip>
                      ) : (
                        <Tooltip title={a.enabled ? "Disable agent" : "Enable agent"}>
                          <Switch checked={a.enabled} disabled={busy === a.id} onChange={() => toggleAgent(a)} />
                        </Tooltip>
                      )
                    }
                  />
                  <CardContent sx={{ pt: 0 }}>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      <Chip
                        size="small"
                        label={seen.online ? "online" : "offline"}
                        sx={{
                          fontWeight: 700,
                          color: seen.online ? "#16a34a" : "#9ca3af",
                          bgcolor: seen.online ? "rgba(22,163,74,0.12)" : "rgba(156,163,175,0.15)",
                          border: "1px solid",
                          borderColor: seen.online ? "rgba(22,163,74,0.4)" : "rgba(156,163,175,0.4)",
                        }}
                      />
                      <Chip
                        size="small"
                        label={declined ? "declined" : a.status === "approve" ? "approved" : a.status}
                        color={a.status === "approve" ? "success" : declined ? "error" : "warning"}
                        variant="outlined"
                      />
                      {a.capabilities.length ? (
                        a.capabilities.map((c) => <CapBadge key={c} cap={c} />)
                      ) : (
                        <Chip size="small" label="no capabilities" variant="outlined" />
                      )}
                      <Tooltip title="View this agent's resources">
                        <Chip
                          size="small"
                          icon={<StorageIcon sx={{ fontSize: 15 }} />}
                          label={`${a.resources_count} resources`}
                          variant="outlined"
                          onClick={() => setResModal({ open: true, id: a.id, title: a.title })}
                          sx={{ cursor: "pointer", fontWeight: 600 }}
                        />
                      </Tooltip>
                    </Stack>
                    {a.resources_count === 0 && !declined && (
                      <Stack
                        direction="row"
                        spacing={1}
                        alignItems="flex-start"
                        sx={{
                          p: 1,
                          mb: 1,
                          borderRadius: 1.5,
                          bgcolor: "rgba(245,158,11,0.1)",
                          border: "1px solid rgba(245,158,11,0.4)",
                        }}
                      >
                        <WarningAmberRoundedIcon sx={{ fontSize: 18, color: "#d97706", mt: "1px" }} />
                        <Typography variant="caption" sx={{ color: "#92400e", lineHeight: 1.45 }}>
                          <b>No resources advertised.</b> This agent can't be routed any cases until it reports
                          what it has — check its <code>resources_available</code> config on the machine.
                        </Typography>
                      </Stack>
                    )}
                    <Divider sx={{ my: 1 }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 700 }}>
                      RUNNING ({a.running_count})
                    </Typography>
                    {a.running.length === 0 ? (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Idle.
                      </Typography>
                    ) : (
                      <Box sx={{ mt: 0.5 }}>
                        {a.running.map((c) => (
                          <CaseRow key={c.id} c={c} onAbort={abortCase} onRequeue={requeueCase} />
                        ))}
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      )}

      <AgentResourcesModal
        open={resModal.open}
        agentId={resModal.id}
        agentTitle={resModal.title}
        onClose={() => setResModal({ open: false })}
      />
    </Box>
  );
};

export default AgentsQueue;
